import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { ScrollArea } from '../ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';
import {
    AlertTriangle,
    CheckCircle,
    XCircle,
    Eye,
    RefreshCw,
    Loader2,
    Shield,
    ShieldAlert,
    ShieldCheck,
    User,
    Clock,
    MessageSquare,
    FileText,
    Filter,
    Search,
    ChevronDown,
    AlertCircle,
    Mic,
    Volume2
} from 'lucide-react';

const NaturalnessReviewPanel = () => {
    const { api } = useAuth();
    const [loading, setLoading] = useState(true);
    const [reviews, setReviews] = useState([]);
    const [stats, setStats] = useState({ pending: 0, approved: 0, rejected: 0, total: 0 });
    const [statusFilter, setStatusFilter] = useState('pending');
    const [selectedReview, setSelectedReview] = useState(null);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);
    const [reviewNotes, setReviewNotes] = useState('');
    const [actionLoading, setActionLoading] = useState(false);
    const [lemonfoxStatus, setLemonfoxStatus] = useState(null);
    const [checkingLemonfox, setCheckingLemonfox] = useState(false);

    useEffect(() => {
        fetchReviews();
    }, [statusFilter]);

    const fetchReviews = async () => {
        setLoading(true);
        try {
            const response = await api.get(`/admin/naturalness-reviews?status=${statusFilter}`);
            setReviews(response.data.reviews || []);
            setStats(response.data.stats || { pending: 0, approved: 0, rejected: 0, total: 0 });
        } catch (error) {
            console.error('Failed to fetch reviews:', error);
            toast.error('Failed to load naturalness reviews');
        } finally {
            setLoading(false);
        }
    };

    const checkLemonfoxStatus = async () => {
        setCheckingLemonfox(true);
        try {
            const response = await api.get('/admin/lemonfox-status');
            setLemonfoxStatus(response.data);
            if (response.data.tts_available) {
                toast.success('Lemonfox API is working');
            } else {
                toast.error(`Lemonfox issue: ${response.data.error || 'Unknown error'}`);
            }
        } catch (error) {
            toast.error('Failed to check Lemonfox status');
        } finally {
            setCheckingLemonfox(false);
        }
    };

    const viewReviewDetail = async (review) => {
        setDetailLoading(true);
        setShowDetailModal(true);
        setReviewNotes('');
        
        try {
            const response = await api.get(`/admin/naturalness-reviews/${review.simulation_id}`);
            setSelectedReview(response.data);
        } catch (error) {
            toast.error('Failed to load review details');
            setShowDetailModal(false);
        } finally {
            setDetailLoading(false);
        }
    };

    const handleApprove = async () => {
        if (!selectedReview) return;
        setActionLoading(true);
        
        try {
            const formData = new FormData();
            if (reviewNotes) formData.append('notes', reviewNotes);
            
            await api.post(
                `/admin/naturalness-reviews/${selectedReview.review.simulation_id}/approve`,
                formData
            );
            
            toast.success('Session approved - marked as legitimate');
            setShowDetailModal(false);
            fetchReviews();
        } catch (error) {
            toast.error('Failed to approve session');
        } finally {
            setActionLoading(false);
        }
    };

    const handleReject = async () => {
        if (!selectedReview) return;
        setActionLoading(true);
        
        try {
            const formData = new FormData();
            if (reviewNotes) formData.append('notes', reviewNotes);
            
            await api.post(
                `/admin/naturalness-reviews/${selectedReview.review.simulation_id}/reject`,
                formData
            );
            
            toast.success('Session rejected - flagged as suspected cheating');
            setShowDetailModal(false);
            fetchReviews();
        } catch (error) {
            toast.error('Failed to reject session');
        } finally {
            setActionLoading(false);
        }
    };

    const getSeverityBadge = (severity) => {
        switch (severity) {
            case 'high':
                return <Badge variant="destructive">High Severity</Badge>;
            case 'medium':
                return <Badge className="bg-amber-500/20 text-amber-700">Medium</Badge>;
            case 'low':
                return <Badge variant="secondary">Low</Badge>;
            default:
                return <Badge variant="outline">{severity}</Badge>;
        }
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'pending':
                return <Badge className="bg-amber-500/20 text-amber-700"><Clock className="h-3 w-3 mr-1" />Pending</Badge>;
            case 'approved':
                return <Badge className="bg-emerald-500/20 text-emerald-700"><CheckCircle className="h-3 w-3 mr-1" />Approved</Badge>;
            case 'rejected':
                return <Badge variant="destructive"><XCircle className="h-3 w-3 mr-1" />Rejected</Badge>;
            default:
                return <Badge variant="outline">{status}</Badge>;
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div>
                    <h2 className="text-2xl font-bold flex items-center gap-2">
                        <ShieldAlert className="h-6 w-6 text-amber-500" />
                        Naturalness Review
                    </h2>
                    <p className="text-muted-foreground">
                        Review sessions flagged for potential LLM-assisted responses
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <Button 
                        variant="outline" 
                        onClick={checkLemonfoxStatus}
                        disabled={checkingLemonfox}
                    >
                        {checkingLemonfox ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                            <Volume2 className="h-4 w-4 mr-2" />
                        )}
                        Check Lemonfox
                    </Button>
                    <Button variant="outline" onClick={fetchReviews} disabled={loading}>
                        <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                        Refresh
                    </Button>
                </div>
            </div>

            {/* Lemonfox Status */}
            {lemonfoxStatus && (
                <Card className={lemonfoxStatus.tts_available ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-red-500/30 bg-red-500/5'}>
                    <CardContent className="py-3">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                {lemonfoxStatus.tts_available ? (
                                    <CheckCircle className="h-5 w-5 text-emerald-500" />
                                ) : (
                                    <AlertCircle className="h-5 w-5 text-red-500" />
                                )}
                                <div>
                                    <p className="font-medium">
                                        Lemonfox TTS: {lemonfoxStatus.tts_available ? 'Online' : 'Offline'}
                                    </p>
                                    {lemonfoxStatus.error && (
                                        <p className="text-sm text-muted-foreground">{lemonfoxStatus.error}</p>
                                    )}
                                </div>
                            </div>
                            <Badge variant={lemonfoxStatus.api_configured ? 'outline' : 'destructive'}>
                                {lemonfoxStatus.api_configured ? 'API Configured' : 'API Not Configured'}
                            </Badge>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card 
                    className={`cursor-pointer transition-colors ${statusFilter === 'pending' ? 'border-amber-500' : ''}`}
                    onClick={() => setStatusFilter('pending')}
                >
                    <CardContent className="p-4 text-center">
                        <Clock className="h-8 w-8 mx-auto mb-2 text-amber-500" />
                        <p className="text-2xl font-bold">{stats.pending}</p>
                        <p className="text-sm text-muted-foreground">Pending Review</p>
                    </CardContent>
                </Card>
                <Card 
                    className={`cursor-pointer transition-colors ${statusFilter === 'approved' ? 'border-emerald-500' : ''}`}
                    onClick={() => setStatusFilter('approved')}
                >
                    <CardContent className="p-4 text-center">
                        <ShieldCheck className="h-8 w-8 mx-auto mb-2 text-emerald-500" />
                        <p className="text-2xl font-bold">{stats.approved}</p>
                        <p className="text-sm text-muted-foreground">Approved</p>
                    </CardContent>
                </Card>
                <Card 
                    className={`cursor-pointer transition-colors ${statusFilter === 'rejected' ? 'border-red-500' : ''}`}
                    onClick={() => setStatusFilter('rejected')}
                >
                    <CardContent className="p-4 text-center">
                        <ShieldAlert className="h-8 w-8 mx-auto mb-2 text-red-500" />
                        <p className="text-2xl font-bold">{stats.rejected}</p>
                        <p className="text-sm text-muted-foreground">Rejected</p>
                    </CardContent>
                </Card>
                <Card 
                    className={`cursor-pointer transition-colors ${statusFilter === 'all' ? 'border-primary' : ''}`}
                    onClick={() => setStatusFilter('all')}
                >
                    <CardContent className="p-4 text-center">
                        <Shield className="h-8 w-8 mx-auto mb-2 text-primary" />
                        <p className="text-2xl font-bold">{stats.total}</p>
                        <p className="text-sm text-muted-foreground">Total Flagged</p>
                    </CardContent>
                </Card>
            </div>

            {/* Reviews List */}
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Filter className="h-5 w-5" />
                        Flagged Sessions
                        <Badge variant="outline" className="ml-2">{reviews.length}</Badge>
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="h-8 w-8 animate-spin text-primary" />
                        </div>
                    ) : reviews.length === 0 ? (
                        <div className="text-center py-12">
                            <ShieldCheck className="h-12 w-12 mx-auto text-emerald-500 mb-3" />
                            <h3 className="font-medium">No Flagged Sessions</h3>
                            <p className="text-muted-foreground">
                                {statusFilter === 'pending' 
                                    ? 'No sessions pending review'
                                    : `No ${statusFilter} sessions found`}
                            </p>
                        </div>
                    ) : (
                        <ScrollArea className="h-[400px]">
                            <div className="space-y-3">
                                {reviews.map((review) => (
                                    <div 
                                        key={review.simulation_id}
                                        className="flex items-center justify-between p-4 rounded-lg border hover:bg-muted/50 transition-colors cursor-pointer"
                                        onClick={() => viewReviewDetail(review)}
                                    >
                                        <div className="flex items-center gap-4">
                                            <div className={`p-2 rounded-full ${
                                                review.status === 'pending' ? 'bg-amber-500/10' :
                                                review.status === 'approved' ? 'bg-emerald-500/10' :
                                                'bg-red-500/10'
                                            }`}>
                                                <AlertTriangle className={`h-5 w-5 ${
                                                    review.status === 'pending' ? 'text-amber-500' :
                                                    review.status === 'approved' ? 'text-emerald-500' :
                                                    'text-red-500'
                                                }`} />
                                            </div>
                                            <div>
                                                <p className="font-medium">{review.skill_name || 'Unknown Skill'}</p>
                                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                                    <User className="h-3 w-3" />
                                                    {review.user_name || 'Unknown User'}
                                                    <span className="text-xs">•</span>
                                                    {review.flagged_at && formatDistanceToNow(new Date(review.flagged_at), { addSuffix: true })}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <div className="text-right">
                                                <p className="text-sm font-medium">
                                                    Score: {review.avg_naturalness_score?.toFixed(1) || '-'}%
                                                </p>
                                                <p className="text-xs text-muted-foreground">
                                                    {review.high_severity_flags?.length || 0} high severity flags
                                                </p>
                                            </div>
                                            {getStatusBadge(review.status)}
                                            <Button variant="ghost" size="icon">
                                                <Eye className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </ScrollArea>
                    )}
                </CardContent>
            </Card>

            {/* Detail Modal */}
            <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
                <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <ShieldAlert className="h-5 w-5 text-amber-500" />
                            Naturalness Review Detail
                        </DialogTitle>
                        <DialogDescription>
                            Review the flagged session and decide whether to approve or reject
                        </DialogDescription>
                    </DialogHeader>

                    {detailLoading ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="h-8 w-8 animate-spin" />
                        </div>
                    ) : selectedReview && (
                        <div className="space-y-6">
                            {/* User & Session Info */}
                            <div className="grid grid-cols-2 gap-4">
                                <Card>
                                    <CardContent className="p-4">
                                        <div className="flex items-center gap-3">
                                            <User className="h-5 w-5 text-primary" />
                                            <div>
                                                <p className="font-medium">{selectedReview.user?.name || 'Unknown'}</p>
                                                <p className="text-sm text-muted-foreground">{selectedReview.user?.email}</p>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                                <Card>
                                    <CardContent className="p-4">
                                        <div className="flex items-center gap-3">
                                            <FileText className="h-5 w-5 text-primary" />
                                            <div>
                                                <p className="font-medium">{selectedReview.review?.skill_name}</p>
                                                <p className="text-sm text-muted-foreground">
                                                    Flagged {selectedReview.review?.flagged_at && format(new Date(selectedReview.review.flagged_at), 'PPp')}
                                                </p>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            </div>

                            {/* Naturalness Scores */}
                            <Card>
                                <CardHeader className="pb-2">
                                    <CardTitle className="text-base">Naturalness Analysis</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="grid grid-cols-3 gap-4 mb-4">
                                        <div className="text-center p-3 rounded-lg bg-muted">
                                            <p className="text-2xl font-bold">{selectedReview.review?.avg_naturalness_score?.toFixed(1) || '-'}%</p>
                                            <p className="text-sm text-muted-foreground">Avg Session Score</p>
                                        </div>
                                        <div className="text-center p-3 rounded-lg bg-muted">
                                            <p className="text-2xl font-bold">{selectedReview.review?.reasoning_score?.toFixed(1) || '-'}%</p>
                                            <p className="text-sm text-muted-foreground">Reasoning Score</p>
                                        </div>
                                        <div className="text-center p-3 rounded-lg bg-muted">
                                            <p className="text-2xl font-bold text-red-500">{selectedReview.review?.high_severity_flags?.length || 0}</p>
                                            <p className="text-sm text-muted-foreground">High Severity Flags</p>
                                        </div>
                                    </div>

                                    {/* Flags List */}
                                    {(selectedReview.review?.high_severity_flags?.length > 0 || selectedReview.review?.reasoning_flags?.length > 0) && (
                                        <div className="space-y-2">
                                            <Label>Detection Flags</Label>
                                            <div className="space-y-2">
                                                {selectedReview.review?.high_severity_flags?.map((flag, idx) => (
                                                    <div key={`high-${idx}`} className="flex items-center gap-2 p-2 rounded bg-red-500/10 border border-red-500/20">
                                                        <AlertTriangle className="h-4 w-4 text-red-500 flex-shrink-0" />
                                                        <span className="text-sm">{flag.message || flag.type || JSON.stringify(flag)}</span>
                                                        <Badge variant="destructive" className="ml-auto">High</Badge>
                                                    </div>
                                                ))}
                                                {selectedReview.review?.reasoning_flags?.map((flag, idx) => (
                                                    <div key={`reason-${idx}`} className="flex items-center gap-2 p-2 rounded bg-amber-500/10 border border-amber-500/20">
                                                        <AlertCircle className="h-4 w-4 text-amber-500 flex-shrink-0" />
                                                        <span className="text-sm">{flag.message || flag.type || JSON.stringify(flag)}</span>
                                                        <Badge className="bg-amber-500/20 text-amber-700 ml-auto">{flag.severity || 'Medium'}</Badge>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>

                            {/* Session Messages */}
                            {selectedReview.simulation?.messages && (
                                <Card>
                                    <CardHeader className="pb-2">
                                        <CardTitle className="text-base flex items-center gap-2">
                                            <MessageSquare className="h-4 w-4" />
                                            Session Transcript
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <ScrollArea className="h-[200px]">
                                            <div className="space-y-3">
                                                {selectedReview.simulation.messages.map((msg, idx) => (
                                                    <div 
                                                        key={idx}
                                                        className={`p-3 rounded-lg ${
                                                            msg.role === 'user' 
                                                                ? 'bg-primary/10 ml-8' 
                                                                : 'bg-muted mr-8'
                                                        }`}
                                                    >
                                                        <div className="flex items-center gap-2 mb-1">
                                                            {msg.role === 'user' ? (
                                                                <User className="h-3 w-3" />
                                                            ) : (
                                                                <Mic className="h-3 w-3" />
                                                            )}
                                                            <span className="text-xs font-medium capitalize">{msg.role}</span>
                                                            {msg.is_voice && <Badge variant="outline" className="text-xs">Voice</Badge>}
                                                            {msg.naturalness_score && (
                                                                <Badge variant={msg.naturalness_score < 70 ? 'destructive' : 'outline'} className="text-xs ml-auto">
                                                                    {msg.naturalness_score.toFixed(0)}%
                                                                </Badge>
                                                            )}
                                                        </div>
                                                        <p className="text-sm">{msg.content}</p>
                                                    </div>
                                                ))}
                                            </div>
                                        </ScrollArea>
                                    </CardContent>
                                </Card>
                            )}

                            {/* Review Notes */}
                            <div className="space-y-2">
                                <Label htmlFor="review-notes">Review Notes (Optional)</Label>
                                <Textarea
                                    id="review-notes"
                                    placeholder="Add any notes about your decision..."
                                    value={reviewNotes}
                                    onChange={(e) => setReviewNotes(e.target.value)}
                                    rows={3}
                                />
                            </div>

                            {/* Actions */}
                            <DialogFooter className="gap-2">
                                <Button variant="outline" onClick={() => setShowDetailModal(false)}>
                                    Cancel
                                </Button>
                                {selectedReview.review?.status === 'pending' && (
                                    <>
                                        <Button 
                                            variant="destructive"
                                            onClick={handleReject}
                                            disabled={actionLoading}
                                        >
                                            {actionLoading ? (
                                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                            ) : (
                                                <XCircle className="h-4 w-4 mr-2" />
                                            )}
                                            Reject (Flag as Cheating)
                                        </Button>
                                        <Button 
                                            className="bg-emerald-600 hover:bg-emerald-700"
                                            onClick={handleApprove}
                                            disabled={actionLoading}
                                        >
                                            {actionLoading ? (
                                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                            ) : (
                                                <CheckCircle className="h-4 w-4 mr-2" />
                                            )}
                                            Approve (Legitimate)
                                        </Button>
                                    </>
                                )}
                            </DialogFooter>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default NaturalnessReviewPanel;
